import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { violationRecordsService, employeesService, violationCategoriesService, notificationsService } from '../../services/supabaseService';

const ViolationRecords = () => {
  const [violations, setViolations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingViolation, setEditingViolation] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const [formData, setFormData] = useState({
    employeeId: '',
    type: '',
    category: 'Attendance',
    points: 0,
    date: new Date()?.toISOString()?.split('T')?.[0],
    description: ''
  });
  
  const [errors, setErrors] = useState({});

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Violation Records', path: '/violation-records' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [violationsData, employeesData, categoriesData] = await Promise.all([
        violationRecordsService?.getAll(),
        employeesService?.getAll(),
        violationCategoriesService?.getAll()
      ]);
      setViolations(violationsData || []);
      setEmployees(employeesData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Auto-populate points and category based on violation type selection
    if (field === 'type') {
      const selectedCategory = categories?.find(cat => cat?.name === value);
      if (selectedCategory) {
        setFormData(prev => ({
          ...prev,
          points: selectedCategory?.points || 0,
          category: selectedCategory?.category || 'Attendance'
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.employeeId) newErrors.employeeId = 'Employee is required';
    if (!formData?.type?.trim()) newErrors.type = 'Violation type is required';
    if (!formData?.date) newErrors.date = 'Date is required';
    if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        const newViolation = await violationRecordsService?.createWithEmployeeUpdate(formData);
        if (newViolation) {
          setViolations(prev => [newViolation, ...prev]);
          setShowForm(false);
          resetForm();
          
          // Reload employees to reflect updated points
          const updatedEmployees = await employeesService?.getAll();
          setEmployees(updatedEmployees || []);
          
          alert('Violation record created successfully! Employee points updated.');
        }
      } catch (error) {
        console.error('Error creating violation:', error);
        alert('Failed to create violation record. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      type: '',
      category: 'Attendance',
      points: 0,
      date: new Date()?.toISOString()?.split('T')?.[0],
      description: ''
    });
    setErrors({});
  };

  const handleDelete = async (violationId) => {
    if (window.confirm('Are you sure you want to delete this violation record?')) {
      try {
        const success = await violationRecordsService?.delete(violationId);
        if (success) {
          setViolations(prev => prev?.filter(v => v?.id !== violationId));
        }
      } catch (error) {
        console.error('Error deleting violation:', error);
      }
    }
  };

  const handleEdit = (violation) => {
    const employee = employees?.find(e => e?.id === violation?.employeeId);
    setEditingViolation(violation);
    setFormData({
      employeeId: violation?.employeeId,
      type: violation?.type,
      category: violation?.category,
      points: violation?.points,
      date: violation?.date,
      description: violation?.description
    });
    setShowEditModal(true);
  };

  const handleUpdateViolation = async (e) => {
    e?.preventDefault();
    if (validateForm()) {
      try {
        const updatedViolation = await violationRecordsService?.updateWithEmployeeRecalculation(
          editingViolation?.id,
          {
            ...formData,
            editedBy: 'Admin'
          }
        );
        
        if (updatedViolation) {
          setViolations(prev => 
            prev?.map(v => v?.id === editingViolation?.id ? updatedViolation : v)
          );
          setShowEditModal(false);
          setEditingViolation(null);
          resetForm();
          
          // Reload employees to reflect updated points
          const updatedEmployees = await employeesService?.getAll();
          setEmployees(updatedEmployees || []);
          
          // Get updated employee data for notification
          const updatedEmployee = updatedEmployees?.find(e => e?.id === formData?.employeeId);
          
          // Create real-time notification for violation edited
          if (updatedEmployee) {
            await notificationsService?.createViolationEditNotification({
              employeeId: formData?.employeeId,
              employeeName: updatedEmployee?.name,
              employeeDepartment: updatedEmployee?.department,
              violationId: editingViolation?.id,
              oldPoints: parseFloat(editingViolation?.points),
              newPoints: parseFloat(formData?.points),
              currentPoints: parseFloat(updatedEmployee?.currentPoints || 0),
              complianceScore: updatedEmployee?.complianceScore || 100,
              complianceStatus: updatedEmployee?.complianceStatus || 'Good'
            });
          }
          
          alert('Violation record updated successfully! Employee points recalculated.');
        }
      } catch (error) {
        console.error('Error updating violation:', error);
        alert('Failed to update violation record. Please try again.');
      }
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingViolation(null);
    resetForm();
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Attendance': return 'text-blue-600 bg-blue-50';
      case 'Conduct': return 'text-purple-600 bg-purple-50';
      case 'Performance': return 'text-orange-600 bg-orange-50';
      case 'Administrative': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredViolations = violations?.filter(v => {
    const employee = employees?.find(e => e?.id === v?.employeeId);
    const employeeName = employee?.name?.toLowerCase() || '';
    const search = searchTerm?.toLowerCase();
    return employeeName?.includes(search) || v?.type?.toLowerCase()?.includes(search);
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />
          
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-lg text-muted-foreground">Loading violations...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6 mt-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Violation Records</h1>
                  <p className="text-muted-foreground">Record and manage employee violations</p>
                </div>
                <Button
                  onClick={() => setShowForm(!showForm)}
                  className="flex items-center gap-2"
                >
                  <Icon name={showForm ? "X" : "Plus"} size={18} />
                  {showForm ? 'Cancel' : 'Add Violation'}
                </Button>
              </div>

              {showForm && (
                <div className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
                  <h2 className="text-xl font-bold text-foreground mb-4">Record New Violation</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          Employee <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={formData?.employeeId}
                          onChange={(e) => handleChange('employeeId', e?.target?.value)}
                          className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                            errors?.employeeId ? 'border-destructive' : 'border-border'
                          }`}
                        >
                          <option value="">Select employee</option>
                          {employees?.map(emp => (
                            <option key={emp?.id} value={emp?.id}>
                              {emp?.name} ({emp?.employeeId})
                            </option>
                          ))}
                        </select>
                        {errors?.employeeId && (
                          <p className="text-xs text-destructive mt-1">{errors?.employeeId}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          Violation Type <span className="text-destructive">*</span>
                        </label>
                        <select
                          value={formData?.type}
                          onChange={(e) => handleChange('type', e?.target?.value)}
                          className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                            errors?.type ? 'border-destructive' : 'border-border'
                          }`}
                        >
                          <option value="">Select violation type</option>
                          {categories?.map(cat => (
                            <option key={cat?.id} value={cat?.name}>
                              {cat?.name} ({cat?.points} pts)
                            </option>
                          ))}
                        </select>
                        {errors?.type && (
                          <p className="text-xs text-destructive mt-1">{errors?.type}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          Category
                        </label>
                        <input
                          type="text"
                          value={formData?.category}
                          readOnly
                          className="w-full px-4 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                          placeholder="Auto-populated from violation type"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          Points
                        </label>
                        <input
                          type="number"
                          value={formData?.points}
                          readOnly
                          className="w-full px-4 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                          placeholder="Auto-populated from violation type"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1.5">
                          Date <span className="text-destructive">*</span>
                        </label>
                        <Input
                          type="date"
                          value={formData?.date}
                          onChange={(e) => handleChange('date', e?.target?.value)}
                          error={errors?.date}
                        />
                        {errors?.date && (
                          <p className="text-xs text-destructive mt-1">{errors?.date}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">
                        Description <span className="text-destructive">*</span>
                      </label>
                      <textarea
                        value={formData?.description}
                        onChange={(e) => handleChange('description', e?.target?.value)}
                        placeholder="Describe the violation..."
                        rows={3}
                        className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                          errors?.description ? 'border-destructive' : 'border-border'
                        }`}
                      />
                      {errors?.description && (
                        <p className="text-xs text-destructive mt-1">{errors?.description}</p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowForm(false);
                          resetForm();
                        }}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1">
                        Create Violation Record
                      </Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="bg-card rounded-lg shadow-sm border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">All Violations</h2>
                  <Input
                    placeholder="Search by employee or type..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    leftIcon="Search"
                    className="w-64"
                  />
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Employee</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Category</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Points</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Description</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredViolations?.map((violation) => {
                        const employee = employees?.find(e => e?.id === violation?.employeeId);
                        return (
                          <tr key={violation?.id} className="hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <div>
                                <div className="font-medium text-foreground">{employee?.name || 'Unknown'}</div>
                                <div className="text-sm text-muted-foreground">{employee?.employeeId || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-foreground">{violation?.type}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getCategoryColor(violation?.category)}`}>
                                {violation?.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-foreground">{violation?.points}</td>
                            <td className="px-4 py-3 text-sm text-foreground">{violation?.date}</td>
                            <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">{violation?.description}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEdit(violation)}
                                  className="p-1.5 hover:bg-muted rounded transition-colors"
                                  title="Edit"
                                >
                                  <Icon name="Edit" size={18} className="text-muted-foreground hover:text-primary" />
                                </button>
                                <button
                                  onClick={() => handleDelete(violation?.id)}
                                  className="p-1.5 hover:bg-muted rounded transition-colors"
                                  title="Delete"
                                >
                                  <Icon name="Trash2" size={18} className="text-muted-foreground hover:text-destructive" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredViolations?.length === 0 && (
                    <div className="text-center py-12">
                      <Icon name="AlertTriangle" size={48} className="mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No violation records found</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg shadow-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Edit Violation Record</h2>
              <button
                onClick={closeEditModal}
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Icon name="X" size={20} className="text-muted-foreground" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateViolation} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Employee <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={formData?.employeeId}
                    onChange={(e) => handleChange('employeeId', e?.target?.value)}
                    className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors?.employeeId ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    <option value="">Select employee</option>
                    {employees?.map(emp => (
                      <option key={emp?.id} value={emp?.id}>
                        {emp?.name} ({emp?.employeeId})
                      </option>
                    ))}
                  </select>
                  {errors?.employeeId && (
                    <p className="text-xs text-destructive mt-1">{errors?.employeeId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Violation Type <span className="text-destructive">*</span>
                  </label>
                  <select
                    value={formData?.type}
                    onChange={(e) => handleChange('type', e?.target?.value)}
                    className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                      errors?.type ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    <option value="">Select violation type</option>
                    {categories?.map(cat => (
                      <option key={cat?.id} value={cat?.name}>
                        {cat?.name} ({cat?.points} pts)
                      </option>
                    ))}
                  </select>
                  {errors?.type && (
                    <p className="text-xs text-destructive mt-1">{errors?.type}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData?.category}
                    readOnly
                    className="w-full px-4 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                    placeholder="Auto-populated from violation type"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Points
                  </label>
                  <input
                    type="number"
                    value={formData?.points}
                    readOnly
                    className="w-full px-4 py-2 border border-border rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
                    placeholder="Auto-populated from violation type"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Date <span className="text-destructive">*</span>
                  </label>
                  <Input
                    type="date"
                    value={formData?.date}
                    onChange={(e) => handleChange('date', e?.target?.value)}
                    error={errors?.date}
                  />
                  {errors?.date && (
                    <p className="text-xs text-destructive mt-1">{errors?.date}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                  Description <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => handleChange('description', e?.target?.value)}
                  placeholder="Describe the violation..."
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors?.description ? 'border-destructive' : 'border-border'
                  }`}
                />
                {errors?.description && (
                  <p className="text-xs text-destructive mt-1">{errors?.description}</p>
                )}
              </div>

              {editingViolation?.editedAt && (
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="text-muted-foreground">
                    <strong>Last edited:</strong> {new Date(editingViolation?.editedAt)?.toLocaleString()} by {editingViolation?.editedBy || 'System'}
                  </p>
                  {editingViolation?.originalPoints && (
                    <p className="text-muted-foreground mt-1">
                      <strong>Original points:</strong> {editingViolation?.originalPoints}
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeEditModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  Update Violation
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViolationRecords;